import { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Button,
  message,
  Space,
  Row,
  Col,
  InputNumber
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';

import { LostAndFoundItem, ItemStatus, ItemCategory, ItemCondition } from '@/types/community/lostAndFound';
import { addLostAndFoundItem, updateLostAndFoundItem } from '@/api/lostAndFound';
import { useAuth } from '@/contexts/AuthContext';
import { BlockApartmentUnitSelector } from '@/components/common/BlockApartmentUnitSelector';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import { ResidentSelector } from '@/components/common/ResidentSelector';
import { VendorSelector } from '@/components/common/VendorSelector';
import type { ImageMetadata, Money } from '@/types/common';

const { TextArea } = Input;

interface LostAndFoundModalProps {
  open: boolean;
  item?: LostAndFoundItem | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const ITEM_CATEGORIES: { value: ItemCategory; label: string }[] = [
  { value: 'electronics', label: 'Electronics' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'documents', label: 'Documents' },
  { value: 'other', label: 'Other' }
];

const ITEM_STATUSES: { value: ItemStatus; label: string }[] = [
  { value: 'found', label: 'Found' },
  { value: 'claimed', label: 'Claimed' },
  { value: 'returned', label: 'Returned' },
  { value: 'disposed', label: 'Disposed' }
];

const ITEM_CONDITIONS: { value: ItemCondition; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' }
];

const PERSON_TYPES = [
  { value: 'resident', label: 'Resident' },
  { value: 'employee', label: 'Employee' },
  { value: 'vendor', label: 'Vendor' }
];

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
  { value: 'INR', label: 'INR' }
];

export default function LostAndFoundModal({ open, item, onCancel }: LostAndFoundModalProps) {
  const { t } = useTranslation(['lostAndFound', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [blockData, setBlockData] = useState<{ id: string; name: string } | null>(null);
  const [apartmentUnitData, setApartmentUnitData] = useState<{ id: string; name: string } | null>(null);
  
  // Reported By state
  const [reportedByType, setReportedByType] = useState<'resident' | 'employee' | 'vendor' | undefined>(undefined);
  const [reportedByData, setReportedByData] = useState<{ id: string; name: string; type: 'resident' | 'employee' | 'vendor' } | null>(null);
  
  // Claimed By state
  const [claimedByType, setClaimedByType] = useState<'resident' | 'employee' | 'vendor' | undefined>(undefined);
  const [claimedByData, setClaimedByData] = useState<{ id: string; name: string; type: 'resident' | 'employee' | 'vendor' } | null>(null);
  
  // Handed Over To state
  const [handedOverToData, setHandedOverToData] = useState<{ id: string; name: string } | null>(null);

  // Mutations
  const addItemMutation = useMutation({
    mutationFn: (data: Omit<LostAndFoundItem, 'id'>) => addLostAndFoundItem(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('lostAndFound:addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['lostAndFound'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Add lost and found item error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:messages.errorOccurred');
      message.error(`Failed to add lost and found item: ${errorMessage}`);
    }
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Omit<LostAndFoundItem, 'id'>> }) => 
      updateLostAndFoundItem(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('lostAndFound:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['lostAndFound'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Update lost and found item error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:messages.errorOccurred');
      message.error(`Failed to update lost and found item: ${errorMessage}`);
    }
  });

  // Initialize form when item changes
  useEffect(() => {
    if (open) {
      if (item) {
        form.setFieldsValue({
          title: item.title,
          description: item.description,
          category: item.category,
          status: item.status,
          condition: item.condition,
          blockId: item.block?.id,
          apartmentUnitId: item.apartmentUnit?.id,
          foundAt: item.foundAt ? dayjs(item.foundAt) : undefined,
          returnedAt: item.returnedAt ? dayjs(item.returnedAt) : undefined,
          valueAmount: item.value?.amount,
          valueCurrency: item.value?.currency || 'USD',
          // Reported By
          reportedByType: item.reportedBy?.type,
          reportedById: item.reportedBy?.id,
          // Claimed By
          claimedByType: item.claimedBy?.type,
          claimedById: item.claimedBy?.id,
          // Handed Over To
          handedOverToId: item.handedOverTo?.id
        });

        // Set related data
        setBlockData(item.block || null);
        setApartmentUnitData(item.apartmentUnit || null);
        setReportedByType(item.reportedBy?.type);
        setReportedByData(item.reportedBy || null);
        setClaimedByType(item.claimedBy?.type);
        setClaimedByData(item.claimedBy || null);
        setHandedOverToData(item.handedOverTo || null);

        // Set file list
        if (item.photos) {
          const files: UploadFile[] = item.photos.map((photo, index) => ({
            uid: `-${index}`,
            name: photo.name,
            status: 'done',
            url: photo.url
          }));
          setFileList(files);
        }
      } else {
        form.resetFields();
        setFileList([]);
        setBlockData(null);
        setApartmentUnitData(null);
        setReportedByType(undefined);
        setReportedByData(null);
        setClaimedByType(undefined);
        setClaimedByData(null);
        setHandedOverToData(null);
      }
    }
  }, [item, form, open]);

  const handleSubmit = async (values: any) => {
    if (!firestoreUser) {
      message.error(t('common:messages.notAuthenticated'));
      return;
    }

    // Additional client-side validation
    if (!values.title?.trim()) {
      message.error(t('lostAndFound:validationErrors.titleRequired'));
      return;
    }
    if (!values.description?.trim()) {
      message.error(t('lostAndFound:validationErrors.descriptionRequired'));
      return;
    }
    if (!values.category) {
      message.error(t('lostAndFound:validationErrors.categoryRequired'));
      return;
    }
    if (!values.status) {
      message.error(t('lostAndFound:validationErrors.statusRequired'));
      return;
    }
    if (!values.condition) {
      message.error(t('lostAndFound:validationErrors.conditionRequired'));
      return;
    }
    if (!values.foundAt) {
      message.error(t('lostAndFound:validationErrors.foundDateRequired'));
      return;
    }

    try {
      // Convert file list to ImageMetadata
      const photos: ImageMetadata[] = fileList
        .filter(file => file.status === 'done')
        .map(file => ({
          id: file.uid || `photo-${Date.now()}-${Math.random()}`,
          name: file.name || '',
          url: file.url || '',
          size: file.size || 0,
          type: file.type || '',
          path: file.url || '',
          uploadedAt: new Date()
        }));

      // Create value object if amount is provided
      const value: Money | undefined = values.valueAmount ? {
        amount: values.valueAmount,
        currency: values.valueCurrency || 'USD'
      } : undefined;

      const itemData: Omit<LostAndFoundItem, 'id'> = {
        title: values.title.trim(),
        description: values.description.trim(),
        category: values.category,
        status: values.status,
        condition: values.condition,
        foundAt: values.foundAt?.toDate() || new Date(),
        block: blockData || undefined,
        apartmentUnit: apartmentUnitData || undefined,
        returnedAt: values.returnedAt?.toDate() || undefined,
        photos: photos.length > 0 ? photos : undefined,
        value: value,
        reportedBy: reportedByData || undefined,
        claimedBy: claimedByData || undefined,
        handedOverTo: handedOverToData || undefined,
        workspace: {
          id: firestoreUser.workspace!.id,
          name: firestoreUser.workspace!.name,
          slug: firestoreUser.workspace!.slug
        }
      };

      console.log('Submitting lost and found item data:', itemData);

      if (item) {
        updateItemMutation.mutate({ id: item.id, data: itemData });
      } else {
        addItemMutation.mutate(itemData);
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      message.error(`Form submission failed: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setBlockData(null);
    setApartmentUnitData(null);
    setReportedByType(undefined);
    setReportedByData(null);
    setClaimedByType(undefined);
    setClaimedByData(null);
    setHandedOverToData(null);
    onCancel();
  };

  const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
  };

  // Render selector based on type
  const renderPersonSelector = (
    type: 'resident' | 'employee' | 'vendor' | undefined,
    onDataChange: (data: any) => void
  ) => {
    switch (type) {
      case 'resident':
        return <ResidentSelector onResidentDataChange={onDataChange} />;
      case 'employee':
        return <EmployeeSelector onEmployeeDataChange={onDataChange} />;
      case 'vendor':
        return <VendorSelector onVendorDataChange={onDataChange} />;
      default:
        return <Select placeholder={t('common:ui.select')} disabled />;
    }
  };

  return (
    <Modal
      title={item ? t('lostAndFound:editItem') : t('lostAndFound:addItem')}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnHidden
    >
      {open && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: 'found',
            condition: 'good',
            category: 'other',
            foundAt: dayjs(),
            valueCurrency: 'USD'
          }}
        >
          <Form.Item
            name="title"
            label={t('lostAndFound:fieldLabels.title')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <Input placeholder={t('lostAndFound:placeholders.titlePlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label={t('lostAndFound:fieldLabels.category')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <Select placeholder={t('lostAndFound:placeholders.selectCategory')}>
                  {ITEM_CATEGORIES.map(category => (
                    <Select.Option key={category.value} value={category.value}>
                      {category.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label={t('lostAndFound:fieldLabels.status')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <Select placeholder={t('lostAndFound:placeholders.selectStatus')}>
                  {ITEM_STATUSES.map(status => (
                    <Select.Option key={status.value} value={status.value}>
                      {status.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="condition"
                label={t('lostAndFound:fieldLabels.condition')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <Select placeholder={t('lostAndFound:placeholders.selectCondition')}>
                  {ITEM_CONDITIONS.map(condition => (
                    <Select.Option key={condition.value} value={condition.value}>
                      {condition.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label={t('lostAndFound:fieldLabels.description')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <TextArea 
              rows={4} 
              placeholder={t('lostAndFound:placeholders.descriptionPlaceholder')} 
            />
          </Form.Item>

          <BlockApartmentUnitSelector
            onBlockDataChange={setBlockData}
            onApartmentUnitDataChange={setApartmentUnitData}
          />

          {/* Reported By Section */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="reportedByType"
                label={t('lostAndFound:fieldLabels.reportedByType')}
              >
                <Select 
                  placeholder={t('lostAndFound:placeholders.selectReportedByType')}
                  onChange={(value) => {
                    setReportedByType(value);
                    setReportedByData(null);
                    form.setFieldsValue({ reportedById: undefined });
                  }}
                  allowClear
                >
                  {PERSON_TYPES.map(type => (
                    <Select.Option key={type.value} value={type.value}>
                      {type.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="reportedById"
                label={t('lostAndFound:fieldLabels.reportedBy')}
              >
                {renderPersonSelector(
                  reportedByType,
                  setReportedByData
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* Claimed By Section */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="claimedByType"
                label={t('lostAndFound:fieldLabels.claimedByType')}
              >
                <Select 
                  placeholder={t('lostAndFound:placeholders.selectClaimedByType')}
                  onChange={(value) => {
                    setClaimedByType(value);
                    setClaimedByData(null);
                    form.setFieldsValue({ claimedById: undefined });
                  }}
                  allowClear
                >
                  {PERSON_TYPES.map(type => (
                    <Select.Option key={type.value} value={type.value}>
                      {type.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="claimedById"
                label={t('lostAndFound:fieldLabels.claimedBy')}
              >
                {renderPersonSelector(
                  claimedByType,
                  setClaimedByData
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* Handed Over To Section */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="handedOverToId"
                label={t('lostAndFound:fieldLabels.handedOverTo')}
              >
                <EmployeeSelector onEmployeeDataChange={setHandedOverToData} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="foundAt"
                label={t('lostAndFound:fieldLabels.foundAt')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <DatePicker 
                  showTime 
                  style={{ width: '100%' }} 
                  placeholder={t('lostAndFound:placeholders.selectFoundAt')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="returnedAt"
                label={t('lostAndFound:fieldLabels.returnedAt')}
              >
                <DatePicker 
                  showTime 
                  style={{ width: '100%' }} 
                  placeholder={t('lostAndFound:placeholders.selectReturnedAt')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="valueAmount"
                label={t('lostAndFound:fieldLabels.value')}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder={t('lostAndFound:placeholders.valuePlaceholder')}
                  min={0}
                  precision={2}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="valueCurrency"
                label={t('lostAndFound:fieldLabels.currency')}
              >
                <Select placeholder={t('lostAndFound:placeholders.selectCurrency')}>
                  {CURRENCY_OPTIONS.map(currency => (
                    <Select.Option key={currency.value} value={currency.value}>
                      {currency.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="photos"
            label={t('lostAndFound:fieldLabels.photos')}
          >
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleFileChange}
              beforeUpload={() => false}
              accept="image/*"
            >
              {fileList.length >= 8 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{t('common:actions.upload')}</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                {t('common:actions.cancel')}
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={addItemMutation.isPending || updateItemMutation.isPending}
              >
                {item ? t('common:actions.update') : t('common:actions.add')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
} 